import { Loader, Select, Stack, TextInput } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { useDebouncedValue } from '@mantine/hooks'
import Form from 'common/Form'
import FeatureInput from 'common/inputs/FeatureInput'
import NumberWithUnitInput from 'common/inputs/NumberWithUnitInput'
import { useSearchProductsSymbols } from 'hooks/products'
import { useEffect, useState } from 'react'
import { Category, Unit } from 'types/products'
import { newShortageOrderSchema } from 'types/schemas'
import { NewShortageOrder } from 'types/shortage'
import { categoriesForSelect, trimFormValues } from 'utils'

type Props = {
  onSubmit: (values: NewShortageOrder) => void
  onCancel: () => void
}

export default function NewShortageOrderForm({ onSubmit, onCancel }: Props) {
  const [searchSymbol, setSearchSymbol] = useState<string>('')
  const [debouncedSearchSymbol] = useDebouncedValue(searchSymbol, 300)

  const products = useSearchProductsSymbols({ symbol: debouncedSearchSymbol })

  const form = useForm<NewShortageOrder>({
    validate: zodResolver(newShortageOrderSchema),
    initialValues: {
      productId: null!,
      category: null!,
      size: undefined!,
      feature: undefined!,
      orderedAmount: undefined!,
      unit: Unit.METER,
    },
    transformValues: trimFormValues,
  })

  useEffect(() => {
    if (form.values.category === Category.FABRIC) {
      form.setFieldValue('unit', Unit.METER)
    } else {
      form.setFieldValue('unit', Unit.PIECE)
    }
  }, [form.values.category])

  return (
    <Form onSubmit={onSubmit} form={form} onCancel={onCancel} submitText="Dodaj zamówienie">
      <Stack gap="xs">
        <Select
          label="Produkt"
          description="Symbol brakującego produktu. Musi być dostępny w systemie."
          placeholder="Wybierz produkt z listy"
          data={products.data?.map((product) =>
            product.alternateSymbol
              ? { label: `${product.symbol} (${product.alternateSymbol})`, value: product.id }
              : { label: product.symbol, value: product.id }
          )}
          disabled={products.isLoading}
          leftSection={products.isLoading ? <Loader size="xs" /> : undefined}
          value={products.isLoading ? 'Ładowanie dostępnych produktów...' : undefined}
          searchValue={searchSymbol}
          onSearchChange={(value) => setSearchSymbol(value)}
          limit={5}
          searchable
          withAsterisk
          nothingFoundMessage="Nie znaleziono produktu o takim symbolu"
          comboboxProps={{ shadow: 'md' }}
          {...(products.isLoading ? {} : form.getInputProps('productId'))}
        />
        <Select
          label="Rodzaj produktu"
          placeholder="Wybierz rodzaj produktu"
          data={categoriesForSelect}
          withAsterisk
          comboboxProps={{ shadow: 'md' }}
          {...form.getInputProps('category')}
        />
        <NumberWithUnitInput
          label="Zamawiana ilość"
          description="Ilość produktu jaką chcesz zamówić. Musi być większa niż suma braków."
          availableUnits={form.values.category === Category.FABRIC ? [Unit.METER] : [Unit.PIECE]}
          amountFieldName="orderedAmount"
          form={form}
          withAsterisk
        />
        <TextInput
          label="Wymiar"
          description="Wymiar brakującego produktu. Wysokość lub wymiary w cm."
          placeholder="250 cm"
          withAsterisk
          {...form.getInputProps('size')}
          onChange={(e) => form.setFieldValue('size', e.currentTarget.value.toLowerCase().replace(/\s+/g, ''))}
        />
        <FeatureInput
          description="Cecha brakującego produktu, np. kolor lub podkład. Braki są grupowane po cesze podczas zamawiania."
          form={form}
        />
      </Stack>
    </Form>
  )
}
